import React from "react";
import styled, { css } from "styled-components";
import LinkExt from "../components/other/LinkExt";
import { stripGens } from "sharedJs__generated/gens";

const P = styled.p<{ type?: "title" }>`
  ${({ type }) =>
    type === "title" &&
    css`
      font-weight: bold;
      margin-top: 35px;
    `}
`;
const Substitude = styled.span`
  position: relative;
  bottom: -2px;
`;

export const includesHighlighter = (text: string, highlighter: string, lang: string) => {
  const highlighters = highlighter ? [highlighter] : [];
  if (lang && highlighter) {
    const striped = stripGens(highlighter, lang);
    if (striped !== highlighter) highlighters.push(striped);
    highlighters.push(stripGens(highlighter, lang));
  }

  return highlighters.find((wordString) => wordString && text.toLowerCase().indexOf(wordString.toLowerCase()) !== -1);
};

const highlight = (text: string, highlighter?: string, lang?: string, hide?: boolean) => {
  if (!text) return null;
  if (!highlighter || !lang) return text;

  const indexdWord = includesHighlighter(text, highlighter, lang);
  if (!indexdWord) return text;

  const index = text.toLowerCase().indexOf(indexdWord.toLowerCase());
  if (index === -1) return text;

  return (
    <>
      {text.substring(0, index)}
      {hide ? <Substitude>____</Substitude> : <strong>{text.substring(index, index + indexdWord.length)}</strong>}
      {highlight(text.substring(index + indexdWord.length), highlighter, lang, hide)}
    </>
  );
};

type Options = {
  before?: React.ReactNode;
  highlighter?: string;
  lang?: string;
  Wrapper?: React.FC;
  hide?: boolean;
};
export const parser = (text: string | null, options?: Options) => {
  const { highlighter, Wrapper, before, lang, hide } = options || {};

  if (!text) return null;
  const paragraphs = text?.split(new RegExp("\\n\\s*\\n*"));
  return paragraphs.map((prgrph, i) => {
    let cleaned = prgrph;
    let type: "title" | undefined;
    if (prgrph.indexOf("# ") === 0) {
      cleaned = prgrph.substring(2);
      type = "title";
    }

    const Component = Wrapper || P;

    const lines = cleaned?.split(new RegExp("\\n"));

    return (
      <Component key={i} type={type}>
        {before && i === 0 && before}
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {highlight(line, highlighter, lang, hide)}
            <br />
          </React.Fragment>
        ))}
      </Component>
    );
  });
};

export const clipboardToAnnotatedText = (clipboardData: DataTransfer): string => {
  const headingsTextR = /<h[1-6].*?>(.*?)<\/h[1-6]>/gi;
  const html = clipboardData.getData("text/html");
  let text = clipboardData.getData("text");

  const headingMatches: string[] = Array.from(html.matchAll(headingsTextR)).map((match) => {
    return match[1];
  });

  headingMatches.forEach((headingText) => {
    text = text.replace(headingText, "# " + headingText);
  });

  return text;
};

// source: https://stackoverflow.com/a/63401250/844628
export const renderWithLinks = (str: string) => {
  const RE_URL = /((\w+:\/\/)|(www\.))\S+/g;
  let match;
  const results: any = [];
  let lastIndex = 0;
  while ((match = RE_URL.exec(str))) {
    const link = match[0];
    if (lastIndex !== match.index) {
      const text = str.substring(lastIndex, match.index);
      results.push(<React.Fragment key={results.length}>{text}</React.Fragment>);
    }
    results.push(
      <LinkExt key={results.length} href={link}>
        {link}
      </LinkExt>
    );
    lastIndex = match.index + link.length;
  }
  if (results.length === 0) {
    return str;
  }
  if (lastIndex !== str.length) {
    results.push(<React.Fragment key={results.length}>{str.substring(lastIndex)}</React.Fragment>);
  }
  return results;
};

export const stripHtml = (html: string): string => {
  if (!html) return "";
  return html.replace(/<\/?[a-z][\s\S]*?>/gi, "");
};

// Clean HTML text by stripping tags and decoding entities
export const cleanHtmlText = (text: string): string => {
  if (!text) return "";

  return stripHtml(text)
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&amp(?=\s|$)/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&deg;/g, "°")
    .replace(/&hellip;/g, "…")
    .replace(/&mdash;/g, "—")
    .replace(/&ndash;/g, "–")
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&#x([0-9A-F]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/\s+/g, " ")
    .trim();
};
